import {
  Box, Card, CardContent, Typography, Button, IconButton, Tooltip, Divider,
} from '@mui/material'
import {
  ForkRight, Compare, ContentCopy, Archive, OpenInNew, PushPin,
} from '@mui/icons-material'
import StatusBadge from '../common/StatusBadge'
import { formatDate, formatNumber } from '../../utils/format'

export default function ScenarioCard({
  scenario,
  onOpen,
  onCompare,
  onBranch,
  onArchive,
  onPin,
  pinned = false,
  compareSelected = false,
}) {
  const { name, description, status, createdBy, createdAt, metrics } = scenario

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: compareSelected ? 'primary.main' : undefined,
        borderWidth: compareSelected ? 2 : 1,
        position: 'relative',
      }}
    >
      {pinned && (
        <PushPin
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: 16,
            color: 'primary.main',
            transform: 'rotate(45deg)',
          }}
        />
      )}

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <ForkRight sx={{ color: 'warning.main', mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>{name}</Typography>
            <StatusBadge status={status} />
          </Box>
        </Box>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          Created by {createdBy} &middot; {formatDate(createdAt)}
        </Typography>

        {metrics && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <MetricItem label="Total Cases" value={formatNumber(metrics.totalCases)} />
              <MetricItem label="Equiv. Cases" value={formatNumber(metrics.totalECs)} />
              <MetricItem
                label="Below DOS"
                value={metrics.belowDOS}
                alert={metrics.belowDOS > 0}
              />
              <MetricItem
                label="Cap. Overloads"
                value={metrics.capacityOverloads}
                alert={metrics.capacityOverloads > 0}
              />
            </Box>
          </>
        )}

        <Box sx={{ mt: 'auto', pt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNew />}
            onClick={() => onOpen?.(scenario)}
          >
            Open
          </Button>
          <Tooltip title="Compare">
            <IconButton
              size="small"
              color={compareSelected ? 'primary' : 'default'}
              onClick={() => onCompare?.(scenario)}
            >
              <Compare />
            </IconButton>
          </Tooltip>
          <Tooltip title="Branch">
            <IconButton size="small" onClick={() => onBranch?.(scenario)}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <Tooltip title={pinned ? 'Unpin' : 'Pin'}>
            <IconButton size="small" onClick={() => onPin?.(scenario)}>
              <PushPin sx={{ transform: pinned ? 'rotate(45deg)' : 'none' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton size="small" onClick={() => onArchive?.(scenario)}>
              <Archive />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  )
}

function MetricItem({ label, value, alert = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="subtitle1"
        sx={{ color: alert ? 'error.main' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  )
}
